import React from 'react';
import { Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { clickableCardSx, RADIUS, staticCardSx } from '../../../theme/tokens';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useParams } from 'react-router-dom';
import { useRouteNavigation } from '../../../hooks/useRouteNavigation';

interface QuickAccessCardProps {
  title: string;
  description?: string;
  icon: IconName;
  href?: string;
  /** Atalhos da home customizada: abre em nova aba mesmo quando o link é interno. */
  openInNewTab?: boolean;
}

export default function QuickAccessCard({ title, description, icon, href = '#', openInNewTab = false }: QuickAccessCardProps) {
  const navigateWithPrefix = useRouteNavigation();
  const params = useParams<{ state?: string; city?: string }>();
  const isDisabled = href === '#';
  const isExternalLink = href?.startsWith('http');
  const opensNewTab = !isDisabled && (isExternalLink || openInNewTab);
  const isInternalRoute = !isDisabled && !isExternalLink && !openInNewTab;

  const getHref = () => {
    if (isDisabled) return undefined;
    if (isExternalLink) return href;

    // Gera o href com prefixo se estamos em uma rota com state/city
    const cleanPath = href.startsWith('/') ? href.substring(1) : href;
    if (params.state && params.city) {
      return `/${params.state}/${params.city}/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    if (isInternalRoute) {
      e.preventDefault();
      navigateWithPrefix(href);
    }
    // Para links externos, deixa o comportamento padrão do navegador
  };

  return (
    <Box
      component="a"
      href={getHref()}
      target={opensNewTab ? '_blank' : undefined}
      rel={opensNewTab ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      aria-disabled={isDisabled || undefined}
      title={description}
      sx={{
        ...(isDisabled ? staticCardSx : clickableCardSx) as object,
        height: '100%',
        minHeight: 72,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        pr: 2,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.55 : 1,
        textDecoration: 'none',
        color: 'text.primary',
        '&:hover .icon-wrapper': {
          backgroundColor: isDisabled ? undefined : 'brand.surfaceStrong',
        },
      }}
    >
      <Box
        className="icon-wrapper"
        aria-hidden
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: `${RADIUS.card - 2}px`,
          backgroundColor: 'brand.surface',
          color: 'brand.text',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 200ms',
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ fontSize: '1.125rem' }} />
      </Box>

      <Typography
        component="span"
        sx={{
          fontWeight: 600,
          fontSize: '0.9375rem',
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {title}
        {opensNewTab && <Box component="span" sx={visuallyHidden}> (abre em nova aba)</Box>}
      </Typography>
    </Box>
  );
}

/** Grade dos atalhos: 2 colunas no mobile, 3 no tablet, 4 a 5 no desktop; itens da linha com a mesma altura. */
export function QuickAccessGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
        },
        gap: { xs: 1.5, md: 2 },
      }}
    >
      {children}
    </Box>
  );
}
